<template>
  <div class="rle" @contextmenu.prevent>
    <!-- Kopfleiste: Werkzeuge · Regler · Aktionen -->
    <div class="rle__bar">
      <div class="rle__group rle__group--title">
        <v-icon small color="#b9a8ff">mdi-layers-outline</v-icon>
        <span class="rle__title">Ebene · {{ activeRegion ? activeRegion.label || activeRegion.id : "" }}</span>
        <span v-if="activeRegion && activeRegion.suggestion && !activeDirty" class="rle__sub rle__sub--hint" title="Dieser Bereich wurde im Detail-Pass übersprungen. Der rohe Render liegt als Ebene mit Deckkraft 0 hier — positionieren, Deckkraft hochziehen, übernehmen.">Vorschlag: Deckkraft hochziehen und positionieren</span>
        <span v-if="dirtyIds.length" class="rle__sub">{{ dirtyIds.length }} geändert</span>
        <span v-if="loadedCount < regionList.length" class="rle__sub rle__sub--muted" title="Übrige Bereiche laden im Hintergrund nach"><v-progress-circular indeterminate size="10" width="2" color="#b9a8ff" class="mr-1" />Ebenen {{ loadedCount }}/{{ regionList.length }}</span>
      </div>

      <div class="rle__group rle__tools">
        <button type="button" class="rle__tool" :class="{ 'rle__tool--on': tool === 'move' }" title="Verschieben / Skalieren / Drehen (V)" @click="setTool('move')"><v-icon small>mdi-cursor-move</v-icon></button>
        <button type="button" class="rle__tool" :class="{ 'rle__tool--on': tool === 'erase' }" title="Sanft löschen (E) — Alt gedrückt = wiederherstellen" @click="setTool('erase')"><v-icon small>mdi-eraser</v-icon></button>
        <button type="button" class="rle__tool" :class="{ 'rle__tool--on': tool === 'restore' }" title="Wiederherstellen (R)" @click="setTool('restore')"><v-icon small>mdi-brush</v-icon></button>
      </div>

      <div v-if="tool !== 'move'" class="rle__group rle__sliders">
        <label class="rle__slider" title="Pinselgröße ( [ / ] )">
          <span>Größe</span>
          <input type="range" min="8" max="800" step="1" :value="brush.size" @input="brush.size = Number($event.target.value)" />
          <b>{{ brush.size }}</b>
        </label>
        <label class="rle__slider" title="Härte des Pinselrands">
          <span>Härte</span>
          <input type="range" min="0" max="100" step="1" :value="Math.round(brush.hardness * 100)" @input="brush.hardness = Number($event.target.value) / 100" />
          <b>{{ Math.round(brush.hardness * 100) }}</b>
        </label>
        <label class="rle__slider" title="Stärke je Strich">
          <span>Stärke</span>
          <input type="range" min="5" max="100" step="1" :value="Math.round(brush.flow * 100)" @input="brush.flow = Number($event.target.value) / 100" />
          <b>{{ Math.round(brush.flow * 100) }}</b>
        </label>
      </div>
      <div v-else class="rle__group rle__sliders">
        <label class="rle__slider" title="Größe der Ebene (oder Ecken im Bild ziehen)">
          <span>Größe</span>
          <input type="range" min="50" max="200" step="0.5" :value="Math.round(xf.scale * 1000) / 10" @mousedown="beginGesture()" @input="setScale(Number($event.target.value) / 100)" />
          <b>{{ Math.round(xf.scale * 100) }} %</b>
        </label>
        <label class="rle__slider" title="Drehung (Alt + Mausrad)">
          <span>Drehung</span>
          <input type="range" min="-45" max="45" step="0.1" :value="xf.rotation" @mousedown="beginGesture()" @input="setRotation(Number($event.target.value))" />
          <b>{{ xf.rotation.toFixed(1) }}°</b>
        </label>
      </div>

      <div class="rle__group rle__sliders">
        <label class="rle__slider" title="Deckkraft der Ebene">
          <span>Deckkraft</span>
          <input type="range" min="0" max="100" step="1" :value="Math.round(xf.opacity * 100)" @mousedown="beginGesture()" @input="setOpacity(Number($event.target.value) / 100)" />
          <b>{{ Math.round(xf.opacity * 100) }}</b>
        </label>
      </div>

      <div class="rle__group">
        <button type="button" class="rle__tool" :class="{ 'rle__tool--on': solo }" title="Solo: nur die aktive Ebene auf Schachbrett — überflüssige Stellen sehen und radieren (S)" @click="solo = !solo"><v-icon small>mdi-image-filter-center-focus</v-icon></button>
        <button type="button" class="rle__tool" :class="{ 'rle__tool--on': onion }" title="Zwiebelhaut: altes Endbild halbtransparent darüber (O)" @click="onion = !onion"><v-icon small>mdi-layers-triple-outline</v-icon></button>
        <button type="button" class="rle__tool" title="Vorher (Leertaste halten)" @mousedown="showBefore = true" @mouseup="showBefore = false" @mouseleave="showBefore = false"><v-icon small>mdi-eye-outline</v-icon></button>
        <button type="button" class="rle__tool" title="Ansicht auf den Bereich (F) · Doppelklick ins Bild" @click="fitView()"><v-icon small>mdi-fit-to-page-outline</v-icon></button>
        <span class="rle__sep"></span>
        <button type="button" class="rle__tool" :disabled="!undoStack.length" title="Rückgängig (⌘Z)" @click="undo"><v-icon small>mdi-undo</v-icon></button>
        <button type="button" class="rle__tool" :disabled="!redoStack.length" title="Wiederholen (⇧⌘Z)" @click="redo"><v-icon small>mdi-redo</v-icon></button>
        <button type="button" class="rle__tool" :disabled="!activeDirty" title="Diese Ebene zurücksetzen" @click="resetAll"><v-icon small>mdi-backup-restore</v-icon></button>
      </div>

      <div class="rle__group rle__actions">
        <button type="button" class="rle__btn" :disabled="saving" title="Abbrechen (Esc)" @click="cancel">Abbrechen</button>
        <button type="button" class="rle__btn rle__btn--primary" :disabled="!dirtyIds.length || saving" :title="dirtyIds.length ? 'Alle geänderten Ebenen als Versionen „Manuell“ einsetzen (⌘⏎)' : 'Noch nichts geändert'" @click="commit">
          <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="#fff" />
          <template v-else>Übernehmen<template v-if="dirtyIds.length > 1"> ({{ dirtyIds.length }})</template></template>
        </button>
      </div>
    </div>

    <div class="rle__body">
      <!-- Seitenleiste: alle Bereiche mit eingesetztem Render (Malte 30.10.: wechseln ohne den Editor zu verlassen) -->
      <aside class="rle__side">
        <div class="rle__sideHead">Bereiche</div>
        <button
            v-for="(r, i) in regions"
            :key="r.id"
            type="button"
            class="rle__item"
            :class="{ 'rle__item--active': r.id === activeId, 'rle__item--dirty': dirtySet[r.id], 'rle__item--error': meta[r.id] && meta[r.id].status === 'error' }"
            :title="(r.label || r.id) + (r.suggestion ? ' — übersprungen: ' + (r.skipped || 'nicht eingesetzt') + '. Render als Vorschlag, Deckkraft startet bei 0' : '') + (meta[r.id] && meta[r.id].error ? ' — ' + meta[r.id].error : '') + ' (Taste ' + (i + 1) + ')'"
            @click="selectRegion(r.id)"
        >
          <span class="rle__thumb">
            <img v-if="r.crops && (r.crops.after || r.crops.rendered || r.crops.before)" :src="r.crops.after || r.crops.rendered || r.crops.before" alt="" draggable="false" />
            <v-progress-circular v-if="meta[r.id] && meta[r.id].status === 'loading'" class="rle__thumbSpin" indeterminate size="18" width="2" color="#fff" />
          </span>
          <span class="rle__itemText">
            <span class="rle__itemLabel">{{ r.label || r.id }}</span>
            <span class="rle__itemSub">
              <template v-if="meta[r.id] && meta[r.id].status === 'error'">Fehler</template>
              <template v-else-if="dirtySet[r.id]">geändert</template>
              <template v-else-if="r.suggestion">Vorschlag · nicht eingesetzt</template>
              <template v-else-if="r.scoreAfter !== null && r.scoreAfter !== undefined">Score {{ r.scoreAfter }}</template>
              <template v-else>&nbsp;</template>
            </span>
          </span>
          <i v-if="dirtySet[r.id]" class="rle__dot"></i>
        </button>
      </aside>

      <!-- Zeichenfläche -->
      <div ref="stage" class="rle__stage" :class="'rle__stage--' + cursorClass">
        <canvas ref="cv" @mousedown="onDown" @mousemove="onMove" @mouseleave="onLeave" @wheel.prevent="onWheel" @dblclick="fitView()"></canvas>
        <div v-if="!ready" class="rle__loading">
          <v-progress-circular v-if="!error" indeterminate size="28" width="3" color="#b9a8ff" />
          <span v-if="!error">{{ loadingText }}</span>
          <template v-else>
            <v-icon color="#ff8a80">mdi-alert-circle-outline</v-icon>
            <span>{{ error }}</span>
            <button type="button" class="rle__btn" @click="cancel">Schließen</button>
          </template>
        </div>
      </div>
    </div>

    <div class="rle__hint">
      <template v-if="tool === 'move'">Ziehen = verschieben · Ecken/Seiten = skalieren · Griff oben = drehen · Rad = Zoom · Alt+Rad = Drehung · Pfeile = 1 px (⇧ 10 px) · Klick auf eine andere Ebene = Bereich wechseln</template>
      <template v-else>Malen = {{ tool === "erase" ? "weich löschen" : "wiederherstellen" }} · Alt = {{ tool === "erase" ? "wiederherstellen" : "löschen" }} · [ ] oder Alt+Rad = Pinselgröße · Rad = Zoom · Rechte Maustaste = Ansicht verschieben</template>
      <span class="rle__hintRight">1–9 / Tab = Bereich</span>
    </div>
  </div>
</template>

<script>
/**
 * Ebenen-Modus für eingesetzte Gesichter (Malte 30.10.2026): Render der aktiven Regions-Version als frei bewegliche
 * Ebene über der Upscale-Grundlage — verschieben, skalieren, drehen, weich radieren, Deckkraft. Seit der zweiten Runde
 * mit Seitenleiste: alle Bereiche des Jobs sind im Editor wählbar, jede Ebene behält ihren Stand, alle geänderten
 * Ebenen bleiben im Bild sichtbar und gehen bei „Übernehmen" in EINEM Job (regionEditCall action manualVersion,
 * functions-upscaler/src/layerEdit.ts) als Versionen „Manuell n" ins Endbild. Kein eigener Compositing-Pfad.
 *
 * Aufbau wie das PSD (Malte 30.10.): unten der körnige Upscale ohne Gesichter, darauf je Bereich der gekörnte Render als weiche
 * Ebene — keine Löcher, keine harten Masken. Das Bild erscheint, sobald Upscale und der angeklickte Bereich da sind, die
 * übrigen Bereiche ziehen nach. Koordinaten: Bildkoordinaten (Pixel des Endbilds) sind die Wahrheit. Ansicht: screen = (img − view.x) · view.zoom.
 * Ebene: Mittelpunkt = Rechteck-Mitte + (dx, dy), dann Drehung, dann Skalierung — alles um den Mittelpunkt.
 * Große Objekte (Bilder, Canvases, Undo-Schnappschüsse) liegen je Bereich in this.L[id] außerhalb der Reaktivität;
 * reaktiv sind nur Werkzeugzustand, die Transformation der AKTIVEN Ebene (xf) und die Meta-Zeile je Bereich (meta).
 */
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import { app } from "../../../fb";
import { currentMonthYear } from "../../utils";
import { EventBus, events } from "../../../event-bus";

const regionalFunctions = getFunctions(app, "europe-west1");
const HANDLE = 7;
const MAX_UNDO = 30;
const freshXf = (opacity = 1) => ({ dx: 0, dy: 0, scale: 1, rotation: 0, opacity });
const BRUSH_KEY = "td_layerBrush";
/** Pinselgröße/Härte/Stärke bleiben über Sitzungen erhalten (Malte 30.10.) */
function loadBrush() {
  const d = { size: 120, hardness: 0.35, flow: 0.6 };
  try {
    const v = JSON.parse(window.localStorage.getItem(BRUSH_KEY) || "null");
    if (v && typeof v === "object") return { size: Math.max(8, Math.min(800, Number(v.size) || d.size)), hardness: Math.max(0, Math.min(1, Number(v.hardness) || d.hardness)), flow: Math.max(0.05, Math.min(1, Number(v.flow) || d.flow)) };
  } catch (e) {
    /* kaputter Eintrag → Standard */
  }
  return d;
}
/** Geändert gegenüber dem Start — Vorschlags-Ebenen starten bei Deckkraft 0, alle anderen bei 1 */
const isDirty = (xf, erased, baseOpacity = 1) => erased || Math.abs(xf.dx) > 0.01 || Math.abs(xf.dy) > 0.01 || Math.abs(xf.scale - 1) > 0.0005 || Math.abs(xf.rotation) > 0.01 || Math.abs(xf.opacity - baseOpacity) > 0.001;

export default {
  name: "RegionLayerEditor",
  props: {
    /** Upscale-Job (abonniert) mit id und stages.final */
    job: { type: Object, required: true },
    /** Bereich, der zuerst offen ist (aus upscaleRegionCards) */
    region: { type: Object, required: true },
    /** Alle wählbaren Bereiche (eingesetzter Render + Rechteck) — Seitenleiste */
    regions: { type: Array, default: () => [] },
    orderName: { type: String, default: "" },
    /** Liefert die layerAssets-Antwort je Bereich (Promise; der Dialog cacht und lädt beim Überfahren vor) */
    getAssets: { type: Function, default: null },
  },
  data() {
    const meta = {};
    for (const r of this.regions.length ? this.regions : [this.region]) meta[r.id] = { status: "idle", error: null, dirty: false };
    return {
      activeId: this.region.id,
      meta,
      error: null,
      loadingText: "Ebene wird vorbereitet …",
      finalReady: false,
      saving: false,
      tool: "move",
      brush: loadBrush(),
      xf: freshXf(),
      erased: false,
      onion: false,
      /** Solo: nur die aktive Ebene auf Schachbrett (ohne Upscale und Nachbarn) */
      solo: false,
      showBefore: false,
      view: { zoom: 1, x: 0, y: 0 },
      undoStack: [],
      redoStack: [],
      hover: null,
      cursorClass: "default",
    };
  },
  computed: {
    regionList() {
      return this.regions.length ? this.regions : [this.region];
    },
    activeRegion() {
      return this.regionList.find((r) => r.id === this.activeId) || this.region;
    },
    activeMeta() {
      return this.meta[this.activeId] || { status: "idle" };
    },
    ready() {
      return this.finalReady && this.activeMeta.status === "ready";
    },
    activeDirty() {
      return isDirty(this.xf, this.erased, this.activeRegion && this.activeRegion.suggestion ? 0 : 1);
    },
    loadedCount() {
      return this.regionList.filter((r) => this.meta[r.id] && this.meta[r.id].status === "ready").length;
    },
    /** Geänderte Bereiche in Bearbeitungsreihenfolge (zuletzt angefasst = oben) — der Server setzt in genau dieser Reihenfolge ein */
    dirtyIds() {
      const ids = this.regionList.filter((r) => (r.id === this.activeId ? this.activeDirty : this.meta[r.id] && this.meta[r.id].dirty)).map((r) => r.id);
      const at = (id) => (this.L[id] && this.L[id].dirtyAt) || 0;
      return ids.sort((a, b) => at(a) - at(b));
    },
    dirtySet() {
      const out = {};
      for (const id of this.dirtyIds) out[id] = true;
      return out;
    },
  },
  created() {
    // Große Objekte außerhalb der Reaktivität halten
    this.L = {}; // je Bereich: { rect, img:{baseCrop,render,hard}, cv:{render,erase,layer,hole}, xf, erased, undo, redo }
    this.finalImg = null;
    this.baseImg = null;
    this.bg = null;
    this.gesture = null;
    this.raf = 0;
    this.bgDirty = true;
  },
  mounted() {
    window.addEventListener("keydown", this.onKey, true);
    window.addEventListener("keyup", this.onKeyUp, true);
    window.addEventListener("mouseup", this.onUp);
    window.addEventListener("mousemove", this.onWindowMove);
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this.$refs.stage);
    this.boot();
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKey, true);
    window.removeEventListener("keyup", this.onKeyUp, true);
    window.removeEventListener("mouseup", this.onUp);
    window.removeEventListener("mousemove", this.onWindowMove);
    if (this.ro) this.ro.disconnect();
    cancelAnimationFrame(this.raf);
  },
  watch: {
    onion() { this.requestDraw(); },
    solo() { this.bgDirty = true; this.requestDraw(); },
    brush: {
      deep: true,
      handler(b) {
        try {
          window.localStorage.setItem(BRUSH_KEY, JSON.stringify(b));
        } catch (e) {
          /* Speicher voll/gesperrt → egal */
        }
      },
    },
    showBefore() { this.bgDirty = true; this.requestDraw(); },
    tool() { this.updateCursor(); },
  },
  methods: {
    // ───────────────────────── Laden ─────────────────────────
    async boot() {
      try {
        await this.loadLayer(this.activeId, true);
        this.$nextTick(() => {
          this.resize();
          this.fitView();
        });
        // Übrige Bereiche im Hintergrund nachladen (Server cacht; Wechsel ist dann sofort)
        for (const r of this.regionList) if (r.id !== this.activeId) await this.loadLayer(r.id).catch(() => {});
      } catch (err) {
        this.error = (err && err.message) || String(err);
      }
    },
    fetchAssets(regionId) {
      const viaDialog = this.getAssets ? this.getAssets(regionId) : null;
      if (viaDialog) return viaDialog.then((res) => (res && res.data) || res);
      const call = httpsCallable(regionalFunctions, "regionEditCall", { timeout: 120000 });
      return call({ action: "layerAssets", parentJobId: this.job.id, regionId }).then((res) => res.data);
    },
    /** Ebene eines Bereichs laden: Server-Vorbereitung + Bilder + Offscreen-Canvases */
    async loadLayer(regionId, first = false) {
      if (this.L[regionId] || this.meta[regionId].status === "loading") return;
      this.$set(this.meta, regionId, { ...this.meta[regionId], status: "loading", error: null });
      try {
        const a = await this.fetchAssets(regionId);
        const rect = { left: a.rect.left, top: a.rect.top, width: a.rect.width, height: a.rect.height };
        if (first) this.loadingText = "Bilder werden geladen …";
        // PSD-Modell (Malte 30.10.): unten der körnige Upscale ohne Gesichter (a.base, Vollbild, einmal), darauf je Bereich der
        // gekörnte Render als weiche Ebene. Das Endbild wird nur für „Vorher" (Leertaste) und die Zwiebelhaut gebraucht.
        const [base, final, render] = await Promise.all([this.baseImg ? Promise.resolve(this.baseImg) : this.loadImage(a.base), this.finalImg ? Promise.resolve(this.finalImg) : this.loadImage(a.final), this.loadImage(a.render)]);
        if (!this.baseImg) {
          this.baseImg = base;
          this.finalImg = final;
          this.W = base.naturalWidth;
          this.H = base.naturalHeight;
          this.finalReady = true;
          this.bgDirty = true;
        }
        const reg = this.regionList.find((r) => r.id === regionId);
        const baseOpacity = reg && reg.suggestion ? 0 : 1;
        const layer = { rect, img: { render }, cv: {}, xf: freshXf(baseOpacity), baseOpacity, erased: false, undo: [], redo: [], order: this.regionList.findIndex((r) => r.id === regionId) };
        this.buildOffscreen(layer);
        this.L[regionId] = layer;
        this.$set(this.meta, regionId, { ...this.meta[regionId], status: "ready" });
        // Jede geladene Ebene wird gezeichnet — der Rest „zieht nach" (Malte), bis dahin zeigt der Upscale dort das rohe Gesicht
        this.requestDraw();
      } catch (err) {
        this.$set(this.meta, regionId, { ...this.meta[regionId], status: "error", error: (err && err.message) || String(err) });
        throw err;
      }
    },
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
        img.src = url;
      });
    },
    mk(w, h) {
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(w));
      c.height = Math.max(1, Math.round(h));
      return c;
    },
    buildOffscreen(layer) {
      const r = layer.rect;
      const render = this.mk(r.width, r.height);
      render.getContext("2d").drawImage(layer.img.render, 0, 0, r.width, r.height);
      const erase = this.mk(r.width, r.height);
      const ec = erase.getContext("2d");
      ec.fillStyle = "#fff";
      ec.fillRect(0, 0, r.width, r.height);
      layer.cv = { render, erase, layer: this.mk(r.width, r.height) };
      this.composeLayer(layer);
    },
    /** Ebene = Render × Radierer-Maske */
    composeLayer(layer) {
      const { render, erase } = layer.cv;
      const c = layer.cv.layer.getContext("2d");
      c.globalCompositeOperation = "source-over";
      c.clearRect(0, 0, render.width, render.height);
      c.drawImage(render, 0, 0);
      c.globalCompositeOperation = "destination-in";
      c.drawImage(erase, 0, 0);
      c.globalCompositeOperation = "source-over";
    },
    /** Aktive Ebene (Canvases etc.) */
    cur() {
      return this.L[this.activeId] || null;
    },

    // ───────────────────────── Bereiche wechseln ─────────────────────────
    selectRegion(regionId) {
      if (regionId === this.activeId || this.saving) return;
      const prev = this.cur();
      if (prev) {
        prev.xf = { ...this.xf };
        prev.erased = this.erased;
        prev.undo = this.undoStack;
        prev.redo = this.redoStack;
        this.$set(this.meta, this.activeId, { ...this.meta[this.activeId], dirty: isDirty(this.xf, this.erased, prev.baseOpacity == null ? 1 : prev.baseOpacity) });
      }
      this.activeId = regionId;
      this.gesture = null;
      const next = this.L[regionId];
      if (next) {
        this.xf = { ...next.xf };
        this.erased = next.erased;
        this.undoStack = next.undo;
        this.redoStack = next.redo;
        this.fitView();
      } else {
        const reg = this.regionList.find((r) => r.id === regionId);
        this.xf = freshXf(reg && reg.suggestion ? 0 : 1);
        this.erased = false;
        this.undoStack = [];
        this.redoStack = [];
        this.loadingText = "Ebene wird vorbereitet …";
        this.loadLayer(regionId)
          .then(() => {
            if (this.activeId === regionId) this.fitView();
          })
          .catch(() => {});
      }
      this.requestDraw();
    },
    /** Transformation eines Bereichs: aktiv = live aus xf, sonst gespeichert */
    xfOf(regionId) {
      return regionId === this.activeId ? this.xf : this.L[regionId].xf;
    },

    // ───────────────────────── Ansicht ─────────────────────────
    resize() {
      const stage = this.$refs.stage;
      const cv = this.$refs.cv;
      if (!stage || !cv) return;
      const dpr = window.devicePixelRatio || 1;
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      if (!w || !h) return;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      cv.style.width = w + "px";
      cv.style.height = h + "px";
      this.dpr = dpr;
      this.sw = w;
      this.sh = h;
      this.bgDirty = true;
      this.requestDraw();
    },
    /** Aktiven Bereich mit Luft in die Ansicht einpassen */
    fitView() {
      const l = this.cur();
      if (!l || !this.sw) return;
      const r = l.rect;
      const pad = 0.45;
      const zoom = Math.min(this.sw / (r.width * (1 + 2 * pad)), this.sh / (r.height * (1 + 2 * pad)), 4);
      this.view = { zoom, x: r.left + r.width / 2 - this.sw / 2 / zoom, y: r.top + r.height / 2 - this.sh / 2 / zoom };
      this.bgDirty = true;
      this.requestDraw();
    },
    zoomAt(factor, sx, sy) {
      const v = this.view;
      const zoom = Math.max(0.05, Math.min(8, v.zoom * factor));
      const ix = v.x + sx / v.zoom;
      const iy = v.y + sy / v.zoom;
      this.view = { zoom, x: ix - sx / zoom, y: iy - sy / zoom };
      this.bgDirty = true;
      this.requestDraw();
    },
    toImg(sx, sy) {
      return { x: this.view.x + sx / this.view.zoom, y: this.view.y + sy / this.view.zoom };
    },
    toScreen(ix, iy) {
      return { x: (ix - this.view.x) * this.view.zoom, y: (iy - this.view.y) * this.view.zoom };
    },
    center(regionId = this.activeId) {
      const r = this.L[regionId].rect;
      const xf = this.xfOf(regionId);
      return { x: r.left + r.width / 2 + xf.dx, y: r.top + r.height / 2 + xf.dy };
    },
    /** Bildkoordinate → Ebenen-Koordinate (Pixel des Renders) der aktiven Ebene */
    toLayer(ix, iy) {
      const l = this.cur();
      const c = this.center();
      const a = (-this.xf.rotation * Math.PI) / 180;
      const dx = (ix - c.x) / this.xf.scale;
      const dy = (iy - c.y) / this.xf.scale;
      return { x: dx * Math.cos(a) - dy * Math.sin(a) + l.rect.width / 2, y: dx * Math.sin(a) + dy * Math.cos(a) + l.rect.height / 2 };
    },
    /** Ecken einer Ebene in Bildkoordinaten (oben links, oben rechts, unten rechts, unten links) */
    corners(regionId = this.activeId) {
      const r = this.L[regionId].rect;
      const xf = this.xfOf(regionId);
      const c = this.center(regionId);
      const a = (xf.rotation * Math.PI) / 180;
      const hw = (r.width / 2) * xf.scale;
      const hh = (r.height / 2) * xf.scale;
      return [
        [-hw, -hh],
        [hw, -hh],
        [hw, hh],
        [-hw, hh],
      ].map(([x, y]) => ({ x: c.x + x * Math.cos(a) - y * Math.sin(a), y: c.y + x * Math.sin(a) + y * Math.cos(a) }));
    },
    insideLayer(ix, iy) {
      const l = this.cur();
      const p = this.toLayer(ix, iy);
      return p.x >= 0 && p.y >= 0 && p.x <= l.rect.width && p.y <= l.rect.height;
    },
    /** Griffe: 4 Ecken + 4 Seitenmitten (alle skalieren gleichmäßig um die Mitte) */
    handlePoints(cs) {
      const mids = cs.map((p, i) => ({ x: (p.x + cs[(i + 1) % 4].x) / 2, y: (p.y + cs[(i + 1) % 4].y) / 2 }));
      return [...cs, ...mids];
    },
    rotateHandle() {
      const cs = this.corners().map((p) => this.toScreen(p.x, p.y));
      const top = { x: (cs[0].x + cs[1].x) / 2, y: (cs[0].y + cs[1].y) / 2 };
      const c = this.toScreen(this.center().x, this.center().y);
      const len = Math.hypot(top.x - c.x, top.y - c.y) || 1;
      return { x: top.x + ((top.x - c.x) / len) * 28, y: top.y + ((top.y - c.y) / len) * 28 };
    },
    /** Was liegt unter dem Zeiger (Screen-Koordinaten)? */
    hitTest(sx, sy) {
      if (!this.ready) return "none";
      if (this.tool === "move") {
        const rh = this.rotateHandle();
        if (Math.hypot(sx - rh.x, sy - rh.y) <= HANDLE + 4) return "rotate";
        const cs = this.corners().map((p) => this.toScreen(p.x, p.y));
        for (const h of this.handlePoints(cs)) if (Math.abs(sx - h.x) <= HANDLE + 3 && Math.abs(sy - h.y) <= HANDLE + 3) return "scale";
        const p = this.toImg(sx, sy);
        if (this.insideLayer(p.x, p.y)) return "move";
        // Klick auf eine andere geladene Ebene wechselt dorthin
        return "pan";
      }
      return "paint";
    },
    /** Bereich unter einer Bildkoordinate (für den Wechsel per Klick) */
    regionAt(ix, iy) {
      for (const r of this.regionList) {
        if (r.id === this.activeId || !this.L[r.id]) continue;
        const l = this.L[r.id];
        const xf = l.xf;
        const c = this.center(r.id);
        const a = (-xf.rotation * Math.PI) / 180;
        const dx = (ix - c.x) / xf.scale;
        const dy = (iy - c.y) / xf.scale;
        const x = dx * Math.cos(a) - dy * Math.sin(a) + l.rect.width / 2;
        const y = dx * Math.sin(a) + dy * Math.cos(a) + l.rect.height / 2;
        if (x >= 0 && y >= 0 && x <= l.rect.width && y <= l.rect.height) return r.id;
      }
      return null;
    },
    updateCursor() {
      const h = this.hover ? this.hitTest(this.hover.sx, this.hover.sy) : "none";
      this.cursorClass = this.gesture ? this.gesture.kind : h;
    },

    // ───────────────────────── Zeichnen ─────────────────────────
    requestDraw() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.draw();
      });
    },
    /** Hintergrund (Endbild + Löcher aller geladenen Ebenen) in Bildschirmauflösung cachen — nur bei Ansichtswechsel neu */
    buildBg() {
      const cv = this.$refs.cv;
      if (!this.bg || this.bg.width !== cv.width || this.bg.height !== cv.height) this.bg = this.mk(cv.width, cv.height);
      const c = this.bg.getContext("2d");
      const v = this.view;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.fillStyle = "#262626";
      c.fillRect(0, 0, this.bg.width, this.bg.height);
      c.setTransform(this.dpr * v.zoom, 0, 0, this.dpr * v.zoom, -v.x * v.zoom * this.dpr, -v.y * v.zoom * this.dpr);
      c.imageSmoothingQuality = "high";
      if (this.solo && !this.showBefore) {
        // Solo: Schachbrett in Bildgröße (Kachel 16 px auf dem Bildschirm, unabhängig vom Zoom)
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.fillStyle = this.checker();
        c.fillRect(0, 0, this.bg.width, this.bg.height);
        this.bgDirty = false;
        return;
      }
      // Unterste Ebene: körniger Upscale ohne eingesetzte Gesichter; „Vorher" (Leertaste) zeigt stattdessen das Endbild
      c.drawImage(this.showBefore ? this.finalImg : this.baseImg, 0, 0);
      this.bgDirty = false;
    },
    checker() {
      if (!this._checker) {
        const t = this.mk(32 * this.dpr, 32 * this.dpr);
        const x = t.getContext("2d");
        const h = 16 * this.dpr;
        x.fillStyle = "#3a3a3c";
        x.fillRect(0, 0, t.width, t.height);
        x.fillStyle = "#2c2c2e";
        x.fillRect(0, 0, h, h);
        x.fillRect(h, h, h, h);
        this._checker = this.$refs.cv.getContext("2d").createPattern(t, "repeat");
      }
      return this._checker;
    },
    /**
     * Welche Ebenen gezeichnet werden (30.10., Malte: Artefakte bei 6 überlappenden Gesichtern): NUR geänderte und die
     * aktive — unberührte Bereiche zeigt das Endbild schon exakt; ihre Loch+Ebene-Rekonstruktion enthält in Überlappungen
     * Nachbarpixel und würde nur Nähte erzeugen. Reihenfolge = Bearbeitungsreihenfolge, die aktive (noch unveränderte) zuletzt.
     */
    drawIds() {
      const dirty = this.dirtyIds.filter((id) => this.L[id]);
      const untouched = this.regionList
        .map((r) => r.id)
        .filter((id) => this.L[id] && !dirty.includes(id))
        .sort((a, b) => this.L[a].order - this.L[b].order);
      return [...untouched, ...dirty];
    },
    drawLayer(ctx, regionId) {
      const l = this.L[regionId];
      const xf = this.xfOf(regionId);
      const c = this.center(regionId);
      ctx.save();
      ctx.globalAlpha = xf.opacity;
      ctx.translate(c.x, c.y);
      ctx.rotate((xf.rotation * Math.PI) / 180);
      ctx.scale(xf.scale, xf.scale);
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(l.cv.layer, -l.rect.width / 2, -l.rect.height / 2);
      ctx.restore();
    },
    draw() {
      const cv = this.$refs.cv;
      if (!cv || !this.finalReady) return;
      const ctx = cv.getContext("2d");
      if (this.bgDirty) this.buildBg();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(this.bg, 0, 0);
      const v = this.view;
      if (!this.showBefore) {
        ctx.setTransform(this.dpr * v.zoom, 0, 0, this.dpr * v.zoom, -v.x * v.zoom * this.dpr, -v.y * v.zoom * this.dpr);
        for (const id of this.solo ? (this.L[this.activeId] ? [this.activeId] : []) : this.drawIds()) this.drawLayer(ctx, id);
        if (this.onion) {
          ctx.globalAlpha = 0.45;
          ctx.drawImage(this.finalImg, 0, 0);
          ctx.globalAlpha = 1;
        }
      }
      // Overlay in Bildschirmkoordinaten
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      if (!this.showBefore) {
        // andere GEÄNDERTE Ebenen: dezenter Rahmen (klickbar) — unberührte bleiben unsichtbar
        for (const r of this.regionList) {
          if (r.id === this.activeId || !this.L[r.id] || !this.dirtySet[r.id]) continue;
          const cs = this.corners(r.id).map((p) => this.toScreen(p.x, p.y));
          ctx.lineWidth = 1;
          ctx.strokeStyle = this.dirtySet[r.id] ? "rgba(185,168,255,0.55)" : "rgba(255,255,255,0.18)";
          ctx.setLineDash([3, 5]);
          ctx.beginPath();
          cs.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
          ctx.closePath();
          ctx.stroke();
          ctx.setLineDash([]);
        }
        if (this.ready) {
          const cs = this.corners().map((p) => this.toScreen(p.x, p.y));
          ctx.lineWidth = 1;
          ctx.strokeStyle = this.tool === "move" ? "rgba(185,168,255,0.95)" : "rgba(185,168,255,0.45)";
          ctx.setLineDash(this.tool === "move" ? [] : [4, 4]);
          ctx.beginPath();
          cs.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
          ctx.closePath();
          ctx.stroke();
          ctx.setLineDash([]);
          if (this.tool === "move") {
            ctx.fillStyle = "#fff";
            ctx.strokeStyle = "#7b5cd6";
            ctx.lineWidth = 1.5;
            for (const p of this.handlePoints(cs)) {
              ctx.beginPath();
              ctx.rect(p.x - HANDLE / 2, p.y - HANDLE / 2, HANDLE, HANDLE);
              ctx.fill();
              ctx.stroke();
            }
            const top = { x: (cs[0].x + cs[1].x) / 2, y: (cs[0].y + cs[1].y) / 2 };
            const rh = this.rotateHandle();
            ctx.beginPath();
            ctx.moveTo(top.x, top.y);
            ctx.lineTo(rh.x, rh.y);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(rh.x, rh.y, HANDLE / 2 + 1, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }
        }
      }
      // Pinsel-Cursor
      if (this.tool !== "move" && this.hover && this.ready) {
        const r = (this.brush.size / 2) * v.zoom;
        ctx.beginPath();
        ctx.arc(this.hover.sx, this.hover.sy, r, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(this.hover.sx, this.hover.sy, Math.max(1, r * this.brush.hardness), 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255,255,255,0.35)";
        ctx.stroke();
      }
    },

    // ───────────────────────── Eingabe ─────────────────────────
    pos(e) {
      const b = this.$refs.cv.getBoundingClientRect();
      return { sx: e.clientX - b.left, sy: e.clientY - b.top };
    },
    onDown(e) {
      if (!this.ready) return;
      e.preventDefault();
      const { sx, sy } = this.pos(e);
      // Rechte/mittlere Maustaste: Ansicht verschieben
      if (e.button === 1 || e.button === 2) {
        this.gesture = { kind: "pan", sx, sy, view: { ...this.view } };
        return;
      }
      const hit = this.hitTest(sx, sy);
      const p = this.toImg(sx, sy);
      if (hit === "paint") {
        this.snapshot();
        const erase = this.tool === "erase" ? !e.altKey : e.altKey;
        this.gesture = { kind: "paint", erase, last: null };
        this.paintTo(p.x, p.y, true);
        this.erased = true;
        return;
      }
      if (hit === "pan") {
        // Ansicht verschieben; ein kurzer Klick ohne Ziehen auf eine andere Ebene wechselt den Bereich (siehe onUp)
        this.gesture = { kind: "pan", sx, sy, view: { ...this.view }, clickRegion: this.regionAt(p.x, p.y), at: Date.now(), moved: 0 };
        return;
      }
      this.snapshot();
      const c = this.center();
      this.gesture = { kind: hit, sx, sy, start: { ...this.xf }, startAngle: Math.atan2(p.y - c.y, p.x - c.x), startDist: Math.hypot(p.x - c.x, p.y - c.y) || 1 };
      this.updateCursor();
    },
    onMove(e) {
      const { sx, sy } = this.pos(e);
      this.hover = { sx, sy };
      if (!this.gesture) {
        this.updateCursor();
        if (this.tool !== "move") this.requestDraw();
      }
    },
    onWindowMove(e) {
      const g = this.gesture;
      if (!g || !this.ready) return;
      const { sx, sy } = this.pos(e);
      this.hover = { sx, sy };
      const p = this.toImg(sx, sy);
      const l = this.cur();
      if (g.kind === "pan") {
        g.moved = Math.max(g.moved || 0, Math.hypot(sx - g.sx, sy - g.sy));
        this.view = { zoom: g.view.zoom, x: g.view.x - (sx - g.sx) / g.view.zoom, y: g.view.y - (sy - g.sy) / g.view.zoom };
        this.bgDirty = true;
      } else if (g.kind === "move") {
        this.xf.dx = g.start.dx + (sx - g.sx) / this.view.zoom;
        this.xf.dy = g.start.dy + (sy - g.sy) / this.view.zoom;
      } else if (g.kind === "scale") {
        const c = { x: l.rect.left + l.rect.width / 2 + g.start.dx, y: l.rect.top + l.rect.height / 2 + g.start.dy };
        const d = Math.hypot(p.x - c.x, p.y - c.y);
        this.xf.scale = Math.max(0.2, Math.min(4, (g.start.scale * d) / g.startDist));
      } else if (g.kind === "rotate") {
        const c = { x: l.rect.left + l.rect.width / 2 + g.start.dx, y: l.rect.top + l.rect.height / 2 + g.start.dy };
        let deg = g.start.rotation + ((Math.atan2(p.y - c.y, p.x - c.x) - g.startAngle) * 180) / Math.PI;
        if (e.shiftKey) deg = Math.round(deg / 5) * 5;
        this.xf.rotation = ((((deg + 180) % 360) + 360) % 360) - 180;
      } else if (g.kind === "paint") {
        this.paintTo(p.x, p.y, false);
      }
      this.requestDraw();
    },
    onUp() {
      if (!this.gesture) return;
      const g = this.gesture;
      this.gesture = null;
      // Klick ohne Ziehen (< 4 px, < 400 ms) auf eine andere Ebene → Bereich wechseln
      if (g.kind === "pan" && g.clickRegion && (g.moved || 0) < 4 && Date.now() - (g.at || 0) < 400) this.selectRegion(g.clickRegion);
      this.updateCursor();
      this.requestDraw();
    },
    onLeave() {
      if (!this.gesture) this.hover = null;
      this.requestDraw();
    },
    onWheel(e) {
      if (!this.ready) return;
      const { sx, sy } = this.pos(e);
      const step = Math.max(-1, Math.min(1, -e.deltaY / 60));
      if (e.ctrlKey || e.metaKey) return this.zoomAt(Math.pow(1.12, step * 1.5), sx, sy);
      // Rad zoomt NUR die Ansicht (Malte 30.10.: Skalieren der Ebene per Rad störte) — Größe über Ecken oder Regler
      if (this.tool === "move") {
        if (e.altKey) {
          this.beginGesture();
          this.setRotation(this.xf.rotation + step * 0.5);
          return;
        }
      } else if (e.altKey) {
        this.brush.size = Math.max(8, Math.min(800, Math.round(this.brush.size * Math.pow(1.1, step))));
        this.requestDraw();
        return;
      }
      this.zoomAt(Math.pow(1.12, step), sx, sy);
    },
    onKey(e) {
      const tag = e.target && e.target.tagName;
      const inInput = tag === "INPUT" || tag === "TEXTAREA" || (e.target && e.target.isContentEditable);
      const meta = e.metaKey || e.ctrlKey;
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        return this.cancel();
      }
      if (inInput && e.key !== "Enter") return;
      // Dialog-Kurztasten (y = Vergleich) nicht durchlassen
      e.stopPropagation();
      if (meta && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        return e.shiftKey ? this.redo() : this.undo();
      }
      if (meta && e.key === "Enter") {
        e.preventDefault();
        return this.commit();
      }
      if (meta) return;
      if (/^[1-9]$/.test(e.key)) {
        const r = this.regionList[Number(e.key) - 1];
        if (r) this.selectRegion(r.id);
        return;
      }
      switch (e.key) {
        case "Tab": {
          e.preventDefault();
          const list = this.regionList;
          const i = list.findIndex((r) => r.id === this.activeId);
          const next = list[(i + (e.shiftKey ? list.length - 1 : 1)) % list.length];
          if (next) this.selectRegion(next.id);
          return;
        }
        case "v": case "V": return this.setTool("move");
        case "e": case "E": return this.setTool("erase");
        case "r": case "R": return this.setTool("restore");
        case "o": case "O": this.onion = !this.onion; return;
        case "s": case "S": this.solo = !this.solo; return;
        case "f": case "F": return this.fitView();
        case "[": this.brush.size = Math.max(8, Math.round(this.brush.size / 1.15)); return this.requestDraw();
        case "]": this.brush.size = Math.min(800, Math.round(this.brush.size * 1.15)); return this.requestDraw();
        case " ":
          e.preventDefault();
          if (!this.showBefore) this.showBefore = true;
          return;
        case "ArrowLeft": case "ArrowRight": case "ArrowUp": case "ArrowDown": {
          e.preventDefault();
          if (!this.ready) return;
          const d = e.shiftKey ? 10 : 1;
          this.beginGesture();
          if (e.key === "ArrowLeft") this.xf.dx -= d;
          if (e.key === "ArrowRight") this.xf.dx += d;
          if (e.key === "ArrowUp") this.xf.dy -= d;
          if (e.key === "ArrowDown") this.xf.dy += d;
          return this.requestDraw();
        }
        default:
      }
    },
    onKeyUp(e) {
      if (e.key === " ") this.showBefore = false;
    },
    setTool(t) {
      this.tool = t;
      this.requestDraw();
    },
    setScale(v) {
      this.xf.scale = Math.max(0.2, Math.min(4, v));
      this.requestDraw();
    },
    setRotation(v) {
      this.xf.rotation = Math.max(-180, Math.min(180, v));
      this.requestDraw();
    },
    setOpacity(v) {
      this.xf.opacity = Math.max(0, Math.min(1, v));
      this.requestDraw();
    },

    // ───────────────────────── Pinsel ─────────────────────────
    /** Weich löschen/wiederherstellen entlang des Strichs auf der aktiven Ebene */
    paintTo(ix, iy, first) {
      const g = this.gesture;
      const l = this.cur();
      const p = this.toLayer(ix, iy);
      const radius = Math.max(1, this.brush.size / 2 / this.xf.scale);
      const points = [];
      if (first || !g.last) points.push(p);
      else {
        const d = Math.hypot(p.x - g.last.x, p.y - g.last.y);
        const spacing = Math.max(1, radius * 0.3);
        const n = Math.max(1, Math.ceil(d / spacing));
        for (let i = 1; i <= n; i++) points.push({ x: g.last.x + ((p.x - g.last.x) * i) / n, y: g.last.y + ((p.y - g.last.y) * i) / n });
      }
      g.last = p;
      const c = l.cv.erase.getContext("2d");
      c.globalCompositeOperation = g.erase ? "destination-out" : "source-over";
      const flow = Math.min(1, this.brush.flow * 0.35 + 0.02);
      for (const q of points) {
        const grad = c.createRadialGradient(q.x, q.y, 0, q.x, q.y, radius);
        grad.addColorStop(0, `rgba(255,255,255,${flow})`);
        grad.addColorStop(Math.max(0, Math.min(0.98, this.brush.hardness)), `rgba(255,255,255,${flow})`);
        grad.addColorStop(1, "rgba(255,255,255,0)");
        c.fillStyle = grad;
        c.beginPath();
        c.arc(q.x, q.y, radius, 0, Math.PI * 2);
        c.fill();
      }
      c.globalCompositeOperation = "source-over";
      this.composeLayer(l);
    },

    // ───────────────────────── Verlauf (je aktive Ebene) ─────────────────────────
    snapshot() {
      const l = this.cur();
      if (!l) return;
      l.dirtyAt = Date.now();
      const c = l.cv.erase.getContext("2d");
      this.undoStack.push({ xf: { ...this.xf }, erased: this.erased, erase: c.getImageData(0, 0, l.cv.erase.width, l.cv.erase.height) });
      if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
      this.redoStack = [];
    },
    /** Regler/Rad/Tasten: ein Schnappschuss je zusammenhängender Änderung (innerhalb 700 ms) */
    beginGesture() {
      const now = Date.now();
      if (this.lastGestureAt && now - this.lastGestureAt < 700) {
        this.lastGestureAt = now;
        return;
      }
      this.lastGestureAt = now;
      this.snapshot();
    },
    restore(s) {
      const l = this.cur();
      this.xf = { ...s.xf };
      this.erased = s.erased;
      l.cv.erase.getContext("2d").putImageData(s.erase, 0, 0);
      this.composeLayer(l);
      this.requestDraw();
    },
    current() {
      const l = this.cur();
      const c = l.cv.erase.getContext("2d");
      return { xf: { ...this.xf }, erased: this.erased, erase: c.getImageData(0, 0, l.cv.erase.width, l.cv.erase.height) };
    },
    undo() {
      if (!this.undoStack.length || !this.ready) return;
      this.redoStack.push(this.current());
      this.restore(this.undoStack.pop());
    },
    redo() {
      if (!this.redoStack.length || !this.ready) return;
      this.undoStack.push(this.current());
      this.restore(this.redoStack.pop());
    },
    resetAll() {
      const l = this.cur();
      if (!l) return;
      this.snapshot();
      this.xf = freshXf(l.baseOpacity == null ? 1 : l.baseOpacity);
      this.erased = false;
      const c = l.cv.erase.getContext("2d");
      c.globalCompositeOperation = "source-over";
      c.fillStyle = "#fff";
      c.fillRect(0, 0, l.cv.erase.width, l.cv.erase.height);
      this.composeLayer(l);
      this.requestDraw();
    },

    // ───────────────────────── Abschluss ─────────────────────────
    cancel() {
      if (this.saving) return;
      if (this.dirtyIds.length && !window.confirm(this.dirtyIds.length > 1 ? `Änderungen an ${this.dirtyIds.length} Ebenen verwerfen?` : "Änderungen an der Ebene verwerfen?")) return;
      this.$emit("close");
    },
    /** Neues Rechteck einer Ebene: altes Rechteck ∪ transformierte Ebene, aufs Bild begrenzt */
    commitRect(regionId) {
      const cs = this.corners(regionId);
      const r = this.L[regionId].rect;
      let left = Math.min(r.left, ...cs.map((p) => p.x));
      let top = Math.min(r.top, ...cs.map((p) => p.y));
      let right = Math.max(r.left + r.width, ...cs.map((p) => p.x));
      let bottom = Math.max(r.top + r.height, ...cs.map((p) => p.y));
      left = Math.max(0, Math.floor(left - 1));
      top = Math.max(0, Math.floor(top - 1));
      right = Math.min(this.W, Math.ceil(right + 1));
      bottom = Math.min(this.H, Math.ceil(bottom + 1));
      return { left, top, width: Math.max(1, right - left), height: Math.max(1, bottom - top) };
    },
    /** Ebene eines Bereichs in voller Auflösung rendern (nur das Rechteck) und hochladen */
    async renderAndUpload(regionId) {
      const l = this.L[regionId];
      const xf = this.xfOf(regionId);
      const rect = this.commitRect(regionId);
      const out = this.mk(rect.width, rect.height);
      const c = out.getContext("2d");
      c.translate(-rect.left, -rect.top);
      const ctr = this.center(regionId);
      c.globalAlpha = xf.opacity;
      c.translate(ctr.x, ctr.y);
      c.rotate((xf.rotation * Math.PI) / 180);
      c.scale(xf.scale, xf.scale);
      c.imageSmoothingQuality = "high";
      c.drawImage(l.cv.layer, -l.rect.width / 2, -l.rect.height / 2);
      const blob = await new Promise((resolve) => out.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("Ebene konnte nicht gerendert werden");
      const safe = String(this.orderName || "job").replace(/[^A-Za-z0-9_-]+/g, "-");
      const path = `templates/${currentMonthYear}/layer-edit/${safe}_${this.job.id}_${regionId}_${Date.now()}.png`;
      const up = await uploadBytes(storageRef(getStorage(), path), blob, { contentType: "image/png" });
      const layer = await getDownloadURL(up.ref);
      const erased = regionId === this.activeId ? this.erased : l.erased;
      return { regionId, rect, layer, grained: true, transform: { dx: xf.dx, dy: xf.dy, scale: xf.scale, rotation: xf.rotation, opacity: xf.opacity, erased } };
    },
    /**
     * Übernehmen: Editor schließt SOFORT (Malte 30.10.), Rendern/Upload/Job-Start laufen im Hintergrund weiter — die
     * Canvases leben in diesem Objekt weiter, auch wenn die Komponente schon zerstört ist. Das Versprechen geht an den
     * Dialog (`commit-started`), damit Preview/Fulfill bis zum Job-Start warten und nicht das alte Bild senden.
     */
    commit() {
      if (!this.finalReady || !this.dirtyIds.length || this.saving) return;
      this.saving = true;
      const ids = [...this.dirtyIds];
      const labels = ids.map((id) => (this.regionList.find((r) => r.id === id) || {}).label || id);
      const jobId = this.job.id;
      const work = (async () => {
        const layers = await Promise.all(ids.map((id) => this.renderAndUpload(id)));
        const call = httpsCallable(regionalFunctions, "regionEditCall", { timeout: 60000 });
        const res = await call({ action: "manualVersion", parentJobId: jobId, layers });
        // Antwort { jobId, regionIds }: der Worker setzt nacheinander ein, die Bereiche zeigen solange „wird gerendert …"
        return { regionIds: ids, labels, count: ids.length, ...(res.data || {}) };
      })();
      work.catch((err) => EventBus.$emit(events.SNACKBAR, { message: `Ebenen konnten nicht übernommen werden (${labels.join(", ")}): ${(err && err.message) || err}`, type: "error" }));
      this.$emit("commit-started", { promise: work, regionIds: ids, labels, count: ids.length, parentJobId: jobId });
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.rle {
  position: absolute;
  inset: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  background: #262626;
  color: #eee;
  user-select: none;

  &__bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 14px;
    padding: 8px 14px;
    background: #1c1c1e;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  }
  &__group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    &--title { gap: 8px; margin-right: 6px; }
  }
  &__title { font-size: 13px; font-weight: 700; letter-spacing: 0.01em; }
  &__sub { font-size: 11px; color: #b9a8ff; }
  &__sub--muted { display: inline-flex; align-items: center; color: rgba(255, 255, 255, 0.5); }
  &__sub--hint { color: #ffd38a; }
  &__tools { padding: 2px; border-radius: 10px; background: rgba(255, 255, 255, 0.05); }
  &__tool {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 28px;
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.75);
    transition: background 0.12s, color 0.12s;
    .v-icon { color: inherit !important; }
    &:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }
    &--on { background: #7b5cd6; color: #fff; &:hover { background: #8a6ee0; } }
    &:disabled { opacity: 0.3; cursor: default; &:hover { background: transparent; } }
  }
  &__sep { width: 1px; height: 18px; margin: 0 4px; background: rgba(255, 255, 255, 0.12); }
  &__sliders { gap: 12px; }
  &__slider {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    span { min-width: 46px; text-align: right; }
    b { min-width: 34px; font-weight: 600; color: rgba(255, 255, 255, 0.85); font-variant-numeric: tabular-nums; }
    input[type="range"] {
      -webkit-appearance: none;
      width: 96px;
      height: 3px;
      border-radius: 2px;
      background: rgba(255, 255, 255, 0.22);
      outline: none;
      cursor: pointer;
      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #fff;
        border: 2px solid #7b5cd6;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
      }
    }
  }
  &__actions { margin-left: auto; gap: 8px; }
  &__btn {
    height: 30px;
    padding: 0 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.08);
    &:hover { background: rgba(255, 255, 255, 0.14); }
    &--primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 112px;
      background: #7b5cd6;
      color: #fff;
      &:hover { background: #8a6ee0; }
    }
    &:disabled { opacity: 0.45; cursor: default; }
  }

  &__body {
    display: flex;
    flex: 1;
    min-height: 0;
  }
  /* Seitenleiste links: Bereiche */
  &__side {
    flex: 0 0 168px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 8px;
    overflow-y: auto;
    background: #1f1f21;
    border-right: 1px solid rgba(255, 255, 255, 0.07);
  }
  &__sideHead {
    padding: 0 6px 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.38);
  }
  &__item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 6px;
    border-radius: 10px;
    text-align: left;
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid transparent;
    transition: background 0.12s, border-color 0.12s;
    &:hover { background: rgba(255, 255, 255, 0.06); }
    &--active { background: rgba(123, 92, 214, 0.22); border-color: rgba(123, 92, 214, 0.7); color: #fff; }
    &--error { color: #ff8a80; }
  }
  &__thumb {
    position: relative;
    flex: 0 0 auto;
    width: 46px;
    height: 46px;
    border-radius: 8px;
    overflow: hidden;
    background: #333;
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }
  &__thumbSpin {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  &__itemText { display: flex; flex-direction: column; min-width: 0; }
  &__itemLabel { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__itemSub { font-size: 10px; color: rgba(255, 255, 255, 0.45); }
  &__item--dirty &__itemSub { color: #b9a8ff; }
  &__dot {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #b9a8ff;
    box-shadow: 0 0 0 2px #1f1f21;
  }

  &__stage {
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    canvas { display: block; }
    &--move canvas { cursor: move; }
    &--scale canvas { cursor: nwse-resize; }
    &--rotate canvas { cursor: grab; }
    &--pan canvas { cursor: grab; }
    &--paint canvas { cursor: none; }
  }
  &__loading {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
    background: rgba(38, 38, 38, 0.85);
  }
  &__hint {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 6px 14px;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.45);
    background: #1c1c1e;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
  &__hintRight { white-space: nowrap; }
}
</style>
